import {
  type MaintenanceEntry,
  statusMeta,
} from '@/mocks/saGlobalMaintenanceCenter';

interface MaintenanceHistoryProps {
  history: MaintenanceEntry[];
  onSelect: (entry: MaintenanceEntry) => void;
}

export default function MaintenanceHistory({ history, onSelect }: MaintenanceHistoryProps) {
  if (history.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
          <i className="ri-history-line text-foreground-400 text-2xl"></i>
        </div>
        <p className="text-foreground-800 text-sm font-semibold mb-1">Sin historial</p>
        <p className="text-foreground-400 text-xs">No hay mantenimientos completados o cancelados aún.</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical timeline line */}
      <div className="absolute left-[19px] top-0 bottom-0 w-px bg-background-200 hidden sm:block"></div>

      <div className="space-y-3">
        {history.map((entry) => {
          const status = statusMeta[entry.status];
          const isCompleted = entry.status === 'completed';
          return (
            <div
              key={entry.id}
              onClick={() => onSelect(entry)}
              className="relative pl-12 sm:pl-14 cursor-pointer group"
            >
              {/* Timeline dot */}
              <div className={`absolute left-[12px] sm:left-[12px] top-4 w-[15px] h-[15px] rounded-full border-2 border-background-50 z-10 ${
                isCompleted ? 'bg-emerald-500' : 'bg-foreground-400'
              }`}></div>

              {/* Card */}
              <div className="bg-background-50 border border-background-200 rounded-xl p-4 hover:border-background-300 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-foreground-500 text-[10px] font-mono font-semibold">{entry.id}</span>
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${status.bg} ${status.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                      {status.label}
                    </span>
                  </div>
                  <span className="text-foreground-400 text-[10px]">
                    {new Date(entry.startDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                    <span className="mx-1">·</span>
                    {new Date(entry.startDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} UTC
                  </span>
                </div>
                <p className="text-foreground-800 text-xs font-semibold mb-1">{entry.name}</p>
                <p className="text-foreground-400 text-[10px] line-clamp-2">{entry.description}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-foreground-400 text-[10px] flex items-center gap-1">
                    <i className="ri-time-line text-[9px]"></i>
                    {entry.duration}
                  </span>
                  {entry.completedBy && (
                    <span className="text-foreground-400 text-[10px] flex items-center gap-1">
                      <i className="ri-user-line text-[9px]"></i>
                      {entry.completedBy}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}